const formatNumber = (value, locale, decimals = 2) => {
  if (value == null || value === '') {
    return null;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return new Intl.NumberFormat(locale || 'it-IT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(numeric);
};

const resolveStationZone = (station) =>
  station?.sectionId
  || station?.locationDescription
  || station?.zoneId
  || 'Zone';

const resolveStationType = (station) =>
  station?.stationType
  || station?.sensorType
  || station?.type
  || '';

export const buildParcelsTable = ({ landInventory, t, locale }) => {
  if (!Array.isArray(landInventory) || landInventory.length === 0) {
    return null;
  }
  const columns = [
    { key: 'parcelId', label: t('reports.review.template.tableHeaders.parcelId') },
    { key: 'sheet', label: t('reports.review.template.tableHeaders.sheet') },
    { key: 'number', label: t('reports.review.template.tableHeaders.parcelNumber') },
    { key: 'area', label: t('reports.review.template.tableHeaders.areaHa') },
    { key: 'crop', label: t('reports.review.template.tableHeaders.crop') },
    { key: 'status', label: t('reports.review.template.tableHeaders.status') },
  ];
  const rows = landInventory.map(parcel => ({
    parcelId: parcel.parcelId || parcel.id || '',
    sheet: parcel.sheetNumber || parcel.sheet || '',
    number: parcel.parcelNumber || parcel.number || '',
    area: formatNumber(parcel.area, locale),
    crop: parcel.cropType || parcel.crop || '',
    status: parcel.status || '',
  }));
  const totalArea = landInventory.reduce((sum, parcel) => sum + (Number(parcel.area) || 0), 0);
  rows.push({
    parcelId: t('reports.review.template.tableHeaders.total'),
    sheet: '',
    number: '',
    area: formatNumber(totalArea, locale),
    crop: '',
    status: '',
    isTotal: true,
  });
  return {
    title: t('reports.review.template.tables.parcels'),
    columns,
    rows,
  };
};

export const buildCropSummaryTable = ({ landInventory, t, locale }) => {
  if (!Array.isArray(landInventory) || landInventory.length === 0) {
    return null;
  }
  const summary = landInventory.reduce((acc, parcel) => {
    const crop = parcel.cropType || parcel.crop || t('reports.review.template.placeholders.unknownCrop');
    const area = Number(parcel.area) || 0;
    if (!acc[crop]) {
      acc[crop] = { crop, parcels: 0, area: 0 };
    }
    acc[crop].parcels += 1;
    acc[crop].area += area;
    return acc;
  }, {});
  const columns = [
    { key: 'crop', label: t('reports.review.template.tableHeaders.crop') },
    { key: 'parcels', label: t('reports.review.template.tableHeaders.parcelsCount') },
    { key: 'area', label: t('reports.review.template.tableHeaders.areaHa') },
  ];
  const summaryValues = Object.values(summary);
  const rows = summaryValues.map(item => ({
    crop: item.crop,
    parcels: item.parcels,
    area: formatNumber(item.area, locale),
  }));
  const totalParcels = summaryValues.reduce((sum, item) => sum + item.parcels, 0);
  const totalArea = summaryValues.reduce((sum, item) => sum + item.area, 0);
  rows.push({
    crop: t('reports.review.template.tableHeaders.total'),
    parcels: totalParcels,
    area: formatNumber(totalArea, locale),
    isTotal: true,
  });
  return {
    title: t('reports.review.template.tables.cropSummary'),
    columns,
    rows,
  };
};

export const buildBuildingsTable = ({ buildings, t, locale }) => {
  if (!Array.isArray(buildings) || buildings.length === 0) {
    return null;
  }
  const columns = [
    { key: 'type', label: t('reports.review.template.tableHeaders.buildingType') },
    { key: 'area', label: t('reports.review.template.tableHeaders.areaSqm') },
    { key: 'purpose', label: t('reports.review.template.tableHeaders.purpose') },
    { key: 'location', label: t('reports.review.template.tableHeaders.location') },
    { key: 'year', label: t('reports.review.template.tableHeaders.constructionYear') },
  ];
  const rows = buildings.map((building) => ({
    type: building.type || '',
    area: formatNumber(building.area, locale),
    purpose: building.purpose || '',
    location: building.location || '',
    year: building.constructionYear || '',
  }));
  return {
    title: t('reports.review.template.tables.buildings'),
    columns,
    rows,
  };
};

export const buildEquipmentTable = ({ means, t, locale }) => {
  if (!Array.isArray(means) || means.length === 0) {
    return null;
  }
  const columns = [
    { key: 'type', label: t('reports.review.template.tableHeaders.meansType') },
    { key: 'quantity', label: t('reports.review.template.tableHeaders.quantity') },
    { key: 'brand', label: t('reports.review.template.tableHeaders.brand') },
    { key: 'model', label: t('reports.review.template.tableHeaders.model') },
    { key: 'year', label: t('reports.review.template.tableHeaders.year') },
  ];
  const rows = means.map((item) => ({
    type: item.type || '',
    quantity: item.quantity || '',
    brand: item.brand || '',
    model: item.model || '',
    year: item.year || '',
  }));
  return {
    title: t('reports.review.template.tables.means'),
    columns,
    rows,
  };
};

export const buildLivestockTable = ({ livestock, t }) => {
  if (!Array.isArray(livestock) || livestock.length === 0) {
    return null;
  }
  const columns = [
    { key: 'type', label: t('reports.review.template.tableHeaders.livestockType') },
    { key: 'category', label: t('reports.review.template.tableHeaders.livestockCategory') },
    { key: 'count', label: t('reports.review.template.tableHeaders.livestockCount') },
    { key: 'location', label: t('reports.review.template.tableHeaders.location') },
  ];
  const rows = livestock.map((item) => ({
    type: item.type || '',
    category: item.category || '',
    count: item.count || '',
    location: item.location || '',
  }));
  return {
    title: t('reports.review.template.tables.livestock'),
    columns,
    rows,
  };
};

export const buildStationCoverageTable = ({ stations, landInventory, t, locale }) => {
  if (!Array.isArray(stations) || stations.length === 0) {
    return null;
  }
  const zonesMap = new Map();
  stations.forEach((station) => {
    const zone = resolveStationZone(station);
    const type = resolveStationType(station);
    if (!zonesMap.has(zone)) {
      zonesMap.set(zone, { zone, retro: 0, campo: 0, total: 0 });
    }
    const entry = zonesMap.get(zone);
    if (type === 'retro_modulo') {
      entry.retro += 1;
    } else if (type === 'campo_aperto') {
      entry.campo += 1;
    }
    entry.total += 1;
  });
  const totalArea = Array.isArray(landInventory)
    ? landInventory.reduce((sum, parcel) => sum + (Number(parcel.area) || 0), 0)
    : 0;
  const zoneCount = zonesMap.size || 1;
  const estimatedArea = totalArea > 0 ? totalArea / zoneCount : null;

  const columns = [
    { key: 'zone', label: t('reports.workspace.blocks.tableHeaders.zone') },
    { key: 'area', label: t('reports.workspace.blocks.tableHeaders.surfaceHa') },
    { key: 'retro', label: t('reports.workspace.blocks.tableHeaders.retroStations') },
    { key: 'campo', label: t('reports.workspace.blocks.tableHeaders.campoStations') },
    { key: 'total', label: t('reports.workspace.blocks.tableHeaders.totalStations') },
    { key: 'ratio', label: t('reports.workspace.blocks.tableHeaders.stationsPerHa') },
  ];

  const rows = Array.from(zonesMap.values()).map((entry) => {
    const area = estimatedArea ? formatNumber(estimatedArea, locale) : null;
    const ratio = estimatedArea ? entry.total / estimatedArea : null;
    return {
      zone: entry.zone,
      area,
      retro: entry.retro,
      campo: entry.campo,
      total: entry.total,
      ratio: ratio != null ? formatNumber(ratio, locale) : null,
    };
  });
  const totals = rows.reduce(
    (acc, row) => ({
      retro: acc.retro + (Number(row.retro) || 0),
      campo: acc.campo + (Number(row.campo) || 0),
      total: acc.total + (Number(row.total) || 0),
    }),
    { retro: 0, campo: 0, total: 0 }
  );
  const totalRatio = totalArea > 0 ? totals.total / totalArea : null;
  rows.push({
    zone: t('reports.review.template.tableHeaders.total'),
    area: totalArea ? formatNumber(totalArea, locale) : null,
    retro: totals.retro,
    campo: totals.campo,
    total: totals.total,
    ratio: totalRatio != null ? formatNumber(totalRatio, locale) : null,
    isTotal: true,
  });

  return {
    title: t('reports.review.template.tables.coverage'),
    columns,
    rows,
  };
};

export const buildStationInventoryTable = ({ stations, t }) => {
  if (!Array.isArray(stations) || stations.length === 0) {
    return null;
  }
  const columns = [
    { key: 'code', label: t('reports.review.template.tableHeaders.stationCode') },
    { key: 'type', label: t('reports.review.template.tableHeaders.stationType') },
    { key: 'location', label: t('reports.review.template.tableHeaders.location') },
    { key: 'status', label: t('reports.review.template.tableHeaders.status') },
  ];
  const rows = stations.slice(0, 12).map((station) => ({
    code: station.stationCode || station.id || '',
    type: station.stationType || station.sensorType || '',
    location: station.locationDescription || '',
    status: station.status || '',
  }));
  return {
    title: t('reports.review.template.tables.stationInventory'),
    columns,
    rows,
  };
};

export const buildGenericTable = ({ tableData, t }) => {
  if (!tableData || !Array.isArray(tableData.columns)) {
    return null;
  }
  const columns = tableData.columns.map((column) => ({
    key: column.key,
    label: column.labelKey ? t(column.labelKey, { defaultValue: column.key }) : column.label || column.key,
  }));
  const rows = Array.isArray(tableData.rows) ? tableData.rows : [];
  return {
    title: tableData.title || t('reports.review.template.tables.supporting'),
    columns,
    rows,
  };
};
